import { useMemo, useState } from "react";

import clsx from "clsx";

import { Modal } from "@shared/modal";

import { DismissComponent } from "./dismiss-component";
import { Notification } from "./notification";
import { Pill } from "./pill";

export enum NotificationType {
  INFO,
  WARNING,
  DANGER,
}

export enum NotificationDismissMode {
  HIDDEN,
  HOVER,
  SHOW,
}

export type MMMNotificationPill = {
  text: string;
  onClick: () => void;
};

export type MMMNotification = {
  id: string;
  mainText: string;
  subText: string;
  type: NotificationType;
  onDismiss?: (id: string) => void;
  dismissMode: NotificationDismissMode;
  pill: MMMNotificationPill | null;
  isActionable: boolean;
};

export function GroupedNotifications({
  notifications,
}: {
  notifications: MMMNotification[];
}) {
  const [showAll, setShowAll] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeNotificationId, setActiveNotificationId] = useState(
    notifications[0].id,
  );

  const onHover = (newValue: boolean) => {
    setIsHovered(newValue);
  };

  const activeNotification = useMemo(() => {
    const notification = notifications.find(
      (n) => n.id === activeNotificationId,
    );
    if (notification) {
      return notification;
    }
    if (!notification && notifications.length) {
      setActiveNotificationId(notifications[0].id);
      return notifications[0];
    }
    return null;
  }, [notifications, activeNotificationId]);

  const activeNotificationIndex = useMemo(() => {
    return notifications.findIndex(
      (notification) => notification.id === activeNotification?.id,
    );
  }, [activeNotification, notifications]);

  const shouldShowDismissComponentForNotification = (
    notification: MMMNotification,
  ) => {
    switch (notification.dismissMode) {
      case NotificationDismissMode.HIDDEN:
        return false;
      case NotificationDismissMode.HOVER:
        return isHovered;
      case NotificationDismissMode.SHOW:
        return true;
      default:
        return false;
    }
  };

  const getNotificationDismissComponent = (notification: MMMNotification) => {
    if (
      shouldShowDismissComponentForNotification(notification) &&
      notification.onDismiss
    ) {
      return (
        <DismissComponent
          onDismiss={() => notification.onDismiss(notification.id)}
        />
      );
    }

    return null;
  };

  const getPillForNotification = (notification: MMMNotification) => {
    const pillData = notification.pill;
    if (pillData) {
      return (
        <Pill
          text={pillData.text}
          onClick={() => {
            setShowAll(false);
            pillData.onClick();
          }}
          type={notification.type}
        />
      );
    }

    return null;
  };

  const getRightSideComponent = (notification: MMMNotification) => {
    if (notifications.length === 1) {
      return getNotificationDismissComponent(notification);
    }
    return (
      <div className="flex justify-center items-center gap-4">
        <div className="flex flex-col justify-center items-start gap-0.5">
          <div className="text-xs font-semibold leading-3 tracking-wide">
            Notification {activeNotificationIndex + 1}/{notifications.length}
          </div>
          <button
            className="text-xxs hover:text-cosmic font-semibold uppercase underline"
            onClick={() => setShowAll(true)}
          >
            View all
          </button>
        </div>
        <div className="flex flex-col items-center justify-center mr-2">
          <i
            className={clsx(
              "fa-sharp fa-regular fa-chevron-up text-sm mb-[-4px]",
              {
                "text-gray": activeNotificationIndex < 1,
                "hover:text-cosmic+1 cursor-pointer":
                  activeNotificationIndex >= 1,
              },
            )}
            onClick={() => {
              if (activeNotificationIndex < 1) {
                return;
              }
              setActiveNotificationId(
                notifications[activeNotificationIndex - 1].id,
              );
            }}
          ></i>
          <i
            className={clsx(
              "fa-sharp fa-regular fa-chevron-down text-sm mt-[-4px]",
              {
                "text-gray":
                  activeNotificationIndex >= notifications.length - 1,
                "hover:text-cosmic+1 cursor-pointer":
                  activeNotificationIndex < notifications.length - 1,
              },
            )}
            onClick={() => {
              if (activeNotificationIndex >= notifications.length - 1) {
                return;
              }
              setActiveNotificationId(
                notifications[activeNotificationIndex + 1].id,
              );
            }}
          ></i>
        </div>
        {shouldShowDismissComponentForNotification(notification) && (
          <div className="h-5 border-r border-gray"></div>
        )}
        {getNotificationDismissComponent(notification)}
      </div>
    );
  };

  if (!activeNotification) {
    return null;
  }

  return (
    <div className="w-full mb-4">
      <Notification
        mainText={activeNotification.mainText}
        subText={activeNotification.subText}
        type={activeNotification.type}
        PillComponent={getPillForNotification(activeNotification)}
        RightSideComponent={getRightSideComponent(activeNotification)}
        onHover={onHover}
      />
      <Modal
        isOpen={showAll}
        onClose={() => setShowAll(false)}
        titleContent={
          <span className="text-xl font-semibold">
            All banner notifications
          </span>
        }
        size="2xl"
      >
        <div className="w-full flex flex-col gap-4 justify-start items-start">
          {notifications.map((notification) => {
            return (
              <Notification
                key={notification.id}
                mainText={notification.mainText}
                subText={notification.subText}
                type={notification.type}
                PillComponent={getPillForNotification(notification)}
                RightSideComponent={getNotificationDismissComponent(
                  notification,
                )}
                onHover={onHover}
              />
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
